import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getMyEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  Event,
  CreateEventData,
  UpdateEventData,
} from '../../services/eventService';
import { useI18n } from '../../i18n/useI18n';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';

const PINK = '#e86a8a';
const PINK_SOFT = '#ff8ba7';

interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEventData | UpdateEventData) => void;
  event?: Event | null;
  isLoading?: boolean;
}

const EventDialog: React.FC<EventDialogProps> = ({
  open,
  onClose,
  onSubmit,
  event,
  isLoading = false,
}) => {
  const { pages: t } = useI18n();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [eventName, setEventName] = useState('');

  useEffect(() => {
    if (event) {
      setEventName(event.eventName);
    } else {
      setEventName('');
    }
  }, [event, open]);

  const handleSubmit = () => {
    if (!eventName.trim()) {
      toast.error('Vui lòng nhập tên sự kiện');
      return;
    }

    onSubmit({ eventName: eventName.trim() });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1.5, sm: 2 },
          width: { xs: 'calc(100% - 24px)', sm: '100%' },
          borderRadius: 3.5,
          overflow: 'hidden',
          backgroundImage: `linear-gradient(180deg, ${alpha(PINK_SOFT, 0.12)} 0%, #fff 28%)`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          fontSize: { xs: '1.1rem', sm: '1.3rem' },
          letterSpacing: '-0.02em',
          pt: 2.25,
          px: { xs: 2, sm: 3 },
        }}
      >
        {event ? t.events.dialogUpdate : t.events.dialogCreate}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <TextField
          label={t.events.nameLabel}
          fullWidth
          required
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          placeholder={t.events.namePlaceholder}
          autoFocus
          sx={{
            mt: 0.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.5,
              bgcolor: alpha(PINK_SOFT, 0.06),
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          fullWidth={isMobile}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
        >
          {t.common.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          fullWidth={isMobile}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 999,
            boxShadow: 'none',
          }}
        >
          {isLoading ? <CircularProgress size={20} /> : event ? t.events.submitUpdate : t.events.submitCreate}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EventManagement: React.FC = () => {
  const { pages: t, formatMessage } = useI18n();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const response = await getMyEvents();
      setEvents(response.events || []);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Không thể tải danh sách sự kiện');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleOpenDialog = (event?: Event) => {
    setSelectedEvent(event || null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleSubmit = async (data: CreateEventData | UpdateEventData) => {
    setIsSubmitting(true);
    try {
      if (selectedEvent) {
        await updateEvent(selectedEvent.eventCode, data as UpdateEventData);
        toast.success('Đã cập nhật sự kiện');
      } else {
        await createEvent(data as CreateEventData);
        toast.success('Đã tạo sự kiện');
      }
      handleCloseDialog();
      loadEvents();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Có lỗi xảy ra');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (event: Event) => {
    if (!window.confirm(formatMessage(t.events.confirmDelete, { name: event.eventName }))) {
      return;
    }

    try {
      await deleteEvent(event.eventCode);
      toast.success('Đã xóa sự kiện');
      loadEvents();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Không thể xóa sự kiện');
    }
  };

  return (
    <UserPageShell>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: { xs: 1.5, sm: 2.5 },
        }}
      >
        <Typography
          component="h1"
          fontWeight={800}
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.85rem' },
            letterSpacing: '-0.03em',
            lineHeight: 1.2,
            minWidth: 0,
          }}
        >
          {t.events.title}
        </Typography>
        <Button
          variant="contained"
          startIcon={!isMobile ? <AddIcon /> : undefined}
          onClick={() => handleOpenDialog()}
          sx={{
            flexShrink: 0,
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 800,
            boxShadow: 'none',
            ...(isMobile ? { minWidth: 40, width: 40, height: 40, p: 0 } : { px: 2 }),
          }}
          aria-label={t.events.createNew}
        >
          {isMobile ? <AddIcon /> : t.events.createNew}
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: PINK }} />
        </Box>
      ) : events.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <EventIcon sx={{ fontSize: 52, color: 'text.secondary', mb: 1.5 }} />
          <Typography
            variant="h6"
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {t.events.empty}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ mt: 1, borderRadius: 999, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
          >
            {t.events.createNew}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 2.5 }}>
          {events.map((event, index) => (
            <Grid item xs={6} sm={6} md={4} key={event._id}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.25, sm: 2 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: alpha(PINK_SOFT, 0.28),
                  boxShadow: `0 8px 22px ${alpha('#bf5f82', 0.07)}`,
                  background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(PINK_SOFT, 0.08)} 100%)`,
                  ...userCardEnterSx(index),
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: { sm: 'translateY(-3px)' },
                    boxShadow: `0 14px 28px ${alpha(PINK, 0.16)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 48, sm: 58 },
                    height: { xs: 48, sm: 58 },
                    borderRadius: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(PINK, 0.14),
                    color: PINK,
                    mb: 1,
                    '& .MuiSvgIcon-root': { fontSize: { xs: 26, sm: 30 } },
                  }}
                >
                  <EventIcon />
                </Box>
                <Typography
                  fontWeight={800}
                  noWrap
                  title={event.eventName}
                  sx={{
                    width: '100%',
                    color: PINK,
                    fontSize: { xs: '0.85rem', sm: '1.05rem' },
                    mb: 0.25,
                  }}
                >
                  {event.eventName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(PINK, 0.65),
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    mb: 1,
                  }}
                >
                  {formatMessage(t.events.code, { code: event.eventCode })}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    mt: 'auto',
                    width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(event)}
                    sx={{
                      color: PINK,
                      bgcolor: alpha(PINK, 0.1),
                      '&:hover': { bgcolor: alpha(PINK, 0.2) },
                    }}
                    aria-label={t.common.edit}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(event)}
                    sx={{
                      color: '#e53935',
                      bgcolor: alpha('#e53935', 0.08),
                      '&:hover': { bgcolor: alpha('#e53935', 0.16) },
                    }}
                    aria-label={t.common.delete}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <EventDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        event={selectedEvent}
        isLoading={isSubmitting}
      />
    </UserPageShell>
  );
};

export default EventManagement;
